'use client'

import { RandomWelcomeProjectFragment } from '@/gql/generated/graphql'
import { cn } from '@/lib/utils'
import { AllPurposeLink } from '@components/all-purpose-link'
import { buttonVariants } from '@components/button'
import { ImageWithCustomLoader } from '@components/image-with-custom-loader'
import { OverlineHeader } from '@components/overline-header'
import { ArrowRightIcon, BodyText, BrandText, DisplayText, HeadlineText } from 'design-configuration/components'
import { capitalizeFirstLetter } from '../../utils/common'
import { aboutRoute, projectShowRoute } from '@utils/routes'
import { TrackedButtonNames } from '@utils/analytics'
import { trackButtonClicked } from '@utils/analytics'

export function Welcome({ projects }: { projects?: RandomWelcomeProjectFragment[] }): React.ReactElement {
  return (
    <section className="p-md sm:p-md md:p-2xl flex flex-col">
      <div className="gap-md flex flex-col lg:flex-row lg:items-end lg:justify-between lg:gap-[120px]">
        <div>
          <OverlineHeader text="THE PREMIER DESTINATION FOR GENERATIVE ART" />
          <DisplayText className="mt-sm">Created by artists. Activated by you.</DisplayText>
          <HeadlineText className="mt-md w-full max-w-[1100px] font-light sm:w-[90%]">
            Art Blocks is where artists publish algorithmic systems that collectors bring to life as one-of-a-kind
            artworks on the blockchain. Start your collection today.
          </HeadlineText>
        </div>
        <AllPurposeLink
          href={aboutRoute}
          className={cn(
            buttonVariants({ colorTheme: 'primary', branded: true }),
            'gap-x-xs flex h-fit w-fit shrink-0 items-center'
          )}
        >
          <BrandText>Learn More</BrandText>
          <ArrowRightIcon className="w-sm h-sm fill-on-primary" />
        </AllPurposeLink>
      </div>
      <div className="gap-sm mt-2xl grid w-full grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
        {projects?.map((project, index) => (
          <div
            key={project.id}
            className={cn(
              'group relative flex w-fit justify-center',
              index === 3 && 'hidden md:block',
              index === 4 && 'hidden lg:block'
            )}
          >
            <AllPurposeLink
              href={projectShowRoute(project.slug)}
              className="flex flex-col items-center"
              onClick={() => {
                trackButtonClicked(TrackedButtonNames.homeAboutTokenImage)
              }}
            >
              <div className="relative">
                <ImageWithCustomLoader
                  src={project.featured_token?.[0]?.media_url ?? ''}
                  alt={project.name ?? ''}
                  width={400}
                  height={800}
                  className="h-auto w-full object-contain object-top [filter:drop-shadow(3px_3px_6px_rgba(0,0,0,0.20))]"
                />
                <div className="gap-y-2xs text-on-surface-variant mt-sm absolute left-0 right-0 top-full hidden flex-col items-center text-center opacity-0 transition-opacity duration-200 group-hover:opacity-100 md:flex">
                  <BodyText className="font-medium">Art Blocks {capitalizeFirstLetter(project.vertical_name)}</BodyText>
                  <BodyText className="italic">{`${project.name} #${project.featured_token?.[0]?.invocation}`}</BodyText>
                  <BodyText>{project.artist_name}</BodyText>
                </div>
              </div>
            </AllPurposeLink>
          </div>
        ))}
      </div>
    </section>
  )
}

export default Welcome
// End of Selection
